import { useCallback, useEffect, useState } from 'react'
import {
  actOnAccount,
  createInvite,
  grantLanguage,
  revokeLanguageAccess,
  deleteAccount,
  listAccounts,
  listInvites,
  revokeInvite,
  type AdminUser,
  type InviteRow,
  type UserAction,
} from '../lib/api'
import { useStore } from '../state/store'
import { LANGUAGE_LIST, languageOf } from '../lib/languages'

function ago(at: number | null): string {
  if (!at) return 'never'
  const days = Math.floor((Date.now() - at) / 86_400_000)
  if (days === 0) return 'today'
  if (days === 1) return 'yesterday'
  if (days < 30) return `${days}d ago`
  return `${Math.floor(days / 30)}mo ago`
}

/**
 * Account management.
 *
 * Each account is a self-contained instance of the app: its own islands,
 * scenarios, practice history, and devices, invisible to everyone else. Nothing
 * here can reach into another account's study material — only create, suspend,
 * or remove the account that holds it.
 */
export function UsersView() {
  const { data } = useStore()
  const token = data.sync.token
  const ui = languageOf(data.settings.activeLanguage).ui

  const [users, setUsers] = useState<AdminUser[] | null>(null)
  const [callerId, setCallerId] = useState<string | null>(null)
  const [busy, setBusy] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  /** True when the server says this account may not manage users. */
  const [denied, setDenied] = useState(false)

  const [invites, setInvites] = useState<InviteRow[]>([])
  const [email, setEmail] = useState('')
  const [name, setName] = useState('')
  const [makeAdmin, setMakeAdmin] = useState(false)

  /** The invitation link, shown once — only its hash is stored. */
  const [reveal, setReveal] = useState<{
    email: string
    url: string
    emailed: boolean
    emailError?: string
  } | null>(null)
  /** A regenerated password, on the rare occasion one is issued directly. */
  const [passwordReveal, setPasswordReveal] = useState<{ email: string; password: string } | null>(
    null,
  )

  const refresh = useCallback(async () => {
    if (!token) return
    const result = await listAccounts(token)
    if (result.ok) {
      setUsers(result.users)
      setCallerId(result.callerId)
      setDenied(false)
      setError(null)
    } else if (result.code === 'not_admin' || result.code === 'disabled') {
      // Say so plainly rather than making the screen vanish from the rail.
      setDenied(true)
      setUsers([])
      setError(null)
    } else {
      setError(result.error)
      setUsers([])
    }

    const pending = await listInvites(token)
    // Defensive: a malformed or unexpected body must not take the whole
    // accounts screen down with a TypeError.
    if (pending.ok && Array.isArray(pending.invites)) {
      setInvites(pending.invites.filter((i) => i.live))
    }
  }, [token])

  useEffect(() => {
    void refresh()
  }, [refresh])

  if (!token) {
    return (
      <div className="empty">
        Sign in to manage accounts.
      </div>
    )
  }

  if (denied) {
    return (
      <>
        <header className="page-head">
          <div className="eyebrow">Accounts</div>
          <h1 className="page-title">
            <span className="page-title-zh">{ui.accounts}</span> Not your department
          </h1>
        </header>
        <div className="empty">
          This account cannot manage other accounts. Ask an administrator if you need to invite
          someone.
          <div className="muted" style={{ fontSize: '0.86rem', marginTop: '0.6rem' }}>
            Your own islands are unaffected — nothing here changes what you can study.
          </div>
        </div>
      </>
    )
  }

  const invite = async () => {
    setBusy('create')
    setError(null)
    const result = await createInvite(token, email.trim(), name.trim(), makeAdmin)
    setBusy(null)

    if (!result.ok) {
      setError(result.error)
      return
    }
    setReveal({
      email: result.invite.email,
      url: result.url,
      emailed: Boolean(result.emailed),
      ...(result.emailError ? { emailError: result.emailError } : {}),
    })
    setEmail('')
    setName('')
    setMakeAdmin(false)
    void refresh()
  }

  const act = async (user: AdminUser, action: UserAction, confirmText?: string) => {
    if (confirmText && !window.confirm(confirmText)) return
    setBusy(user.id)
    setError(null)
    const result = await actOnAccount(token, user.id, action)
    setBusy(null)

    if (!result.ok) {
      setError(result.error)
      return
    }
    if (result.password) setPasswordReveal({ email: user.email, password: result.password })
    void refresh()
  }

  const toggleLanguage = async (user: AdminUser, code: string, held: boolean) => {
    if (held) {
      const ok = window.confirm(
        `Remove ${code.toUpperCase()} from ${user.email}? They lose access to that site immediately; their islands are kept.`,
      )
      if (!ok) return
    }
    setBusy(user.id)
    setError(null)
    const result = held
      ? await revokeLanguageAccess(token, user.id, code)
      : await grantLanguage(token, user.id, code)
    setBusy(null)
    if (!result.ok) setError(result.error)
    void refresh()
  }

  const remove = async (user: AdminUser) => {
    const ok = window.confirm(
      `Delete ${user.email}? This erases their ${user.records} record${
        user.records === 1 ? '' : 's'
      } — islands, scenarios, and practice history — permanently. There is no undo.`,
    )
    if (!ok) return

    setBusy(user.id)
    setError(null)
    const result = await deleteAccount(token, user.id)
    setBusy(null)
    if (!result.ok) setError(result.error)
    void refresh()
  }

  return (
    <>
      <header className="page-head">
        <div className="eyebrow">Accounts</div>
        <h1 className="page-title">
          <span className="page-title-zh">{ui.accounts}</span> Who has an island
        </h1>
        <p className="lede">
          Every account is its own instance: separate islands, scenarios, history, and devices. You
          can create, suspend, and remove accounts here — you cannot see inside one.
        </p>
      </header>

      {error && (
        <div className="notice is-error" style={{ marginBottom: '1rem' }}>
          {error}
        </div>
      )}

      {reveal && (
        <div className="reveal appear" style={{ marginBottom: '1.2rem' }}>
          <div className="stat-label">
            {reveal.emailed ? `Invitation sent to ${reveal.email}` : `Invitation link for ${reveal.email}`}
          </div>
          <div className="reveal-value">{reveal.url}</div>
          <p className="muted" style={{ fontSize: '0.84rem', margin: '0.5rem 0 0.6rem' }}>
            {reveal.emailed
              ? 'Sent from hello@language-island.com. The link is here too, in case it lands in a spam folder — it works once and expires in seven days.'
              : 'Send them this yourself. They choose their own password — you will never see it. The link works once and expires in seven days.'}
          </p>
          {!reveal.emailed && (
            /* The invitation is real either way; only the delivery failed. Saying
               so plainly beats a silent "sent" that never arrived. */
            <p className="notice is-error" style={{ fontSize: '0.84rem', margin: '0 0 0.6rem' }}>
              Could not email it{reveal.emailError ? `: ${reveal.emailError}` : ''}. Copy the link
              and send it yourself.
            </p>
          )}
          <div className="row">
            <button
              type="button"
              className="btn is-small is-primary"
              onClick={() => void navigator.clipboard?.writeText(reveal.url)}
            >
              Copy link
            </button>
            <button type="button" className="btn is-small is-quiet" onClick={() => setReveal(null)}>
              Done
            </button>
          </div>
        </div>
      )}

      {passwordReveal && (
        <div className="reveal appear" style={{ marginBottom: '1.2rem' }}>
          <div className="stat-label">New password for {passwordReveal.email}</div>
          <div className="reveal-value">{passwordReveal.password}</div>
          <p className="muted" style={{ fontSize: '0.84rem', margin: '0.5rem 0 0.6rem' }}>
            Shown once. Only a hash is stored, so it cannot be looked up — copy it now, or issue
            another.
          </p>
          <div className="row">
            <button
              type="button"
              className="btn is-small"
              onClick={() => void navigator.clipboard?.writeText(passwordReveal.password)}
            >
              Copy
            </button>
            <button
              type="button"
              className="btn is-small is-quiet"
              onClick={() => setPasswordReveal(null)}
            >
              Done
            </button>
          </div>
        </div>
      )}

      <div className="card">
        <div className="stat-label" style={{ marginBottom: '0.7rem' }}>
          Invite someone
        </div>
        <div className="grid grid-2">
          <label className="field">
            <span className="field-label">Email</span>
            <input
              className="input"
              type="email"
              inputMode="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="someone@example.com"
            />
          </label>
          <label className="field">
            <span className="field-label">Name (optional)</span>
            <input
              className="input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Their name"
            />
          </label>
        </div>

        <label className="checkline">
          <input
            type="checkbox"
            checked={makeAdmin}
            onChange={(e) => setMakeAdmin(e.target.checked)}
          />
          <span>
            Can manage accounts
            <span className="muted" style={{ fontSize: '0.84rem' }}>
              {' '}
              — they get this screen too
            </span>
          </span>
        </label>

        <div className="row" style={{ marginTop: '0.6rem' }}>
          <button
            type="button"
            className="btn is-primary"
            disabled={busy === 'create' || !email.includes('@')}
            onClick={() => void invite()}
          >
            {busy === 'create' ? 'Creating link…' : 'Create invitation'}
          </button>
          <span className="muted" style={{ fontSize: '0.84rem' }}>
            You get a link to send. They set their own password.
          </span>
        </div>
      </div>

      {invites.length > 0 && (
        <div className="card">
          <div className="stat-label" style={{ marginBottom: '0.4rem' }}>
            {invites.length} invitation{invites.length === 1 ? '' : 's'} waiting
          </div>
          {invites.map((row) => (
            <div key={row.id} className="user-row">
              <div className="user-main">
                <div className="user-email">{row.email}</div>
                <div className="user-meta">
                  {row.name ? `${row.name} · ` : ''}expires in{' '}
                  {Math.max(0, Math.ceil((row.expiresAt - Date.now()) / 86_400_000))}d
                  {row.isAdmin ? ' · will be an admin' : ''}
                </div>
              </div>
              <button
                type="button"
                className="btn is-small is-quiet is-danger"
                onClick={async () => {
                  if (!window.confirm(`Cancel the invitation to ${row.email}?`)) return
                  const result = await revokeInvite(token, row.id)
                  if (!result.ok) setError(result.error)
                  void refresh()
                }}
              >
                Cancel
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="card">
        <div className="stat-label" style={{ marginBottom: '0.4rem' }}>
          {users === null ? 'Loading…' : `${users.length} account${users.length === 1 ? '' : 's'}`}
        </div>

        {users?.map((user) => {
          const isSelf = user.id === callerId
          const working = busy === user.id
          return (
            <div key={user.id} className={`user-row ${user.disabled ? 'is-off' : ''}`}>
              <div className="user-main">
                <div className="user-email">
                  {user.email}
                  {isSelf && <span className="badge" style={{ marginLeft: '0.5rem' }}>you</span>}
                  {user.isAdmin && (
                    <span className="badge is-ready" style={{ marginLeft: '0.4rem' }}>
                      admin
                    </span>
                  )}
                  {user.disabled && (
                    <span className="badge is-due" style={{ marginLeft: '0.4rem' }}>
                      suspended
                    </span>
                  )}
                </div>
                <div className="user-meta">
                  {user.name ? `${user.name} · ` : ''}
                  {user.records} records · {user.devices} device
                  {user.devices === 1 ? '' : 's'} · seen {ago(user.lastSeenAt)}
                </div>

                {/* Comp first, because it answers the whole question: this
                    account pays nothing and holds everything, including any
                    language added later. The per-language chips below are for
                    the narrower case of giving somebody one of them. */}
                <button
                  type="button"
                  className={`btn is-small ${user.comped ? 'is-primary' : ''}`}
                  disabled={busy === user.id}
                  style={{ marginBottom: '0.6rem' }}
                  title={
                    user.comped
                      ? 'Free access to every language, with no expiry. Click to end it.'
                      : 'Give free access to every language, now and in future, with no expiry.'
                  }
                  onClick={() =>
                    void act(
                      user,
                      user.comped ? 'uncomp' : 'comp',
                      user.comped
                        ? `End free access for ${user.email}? Anything they have paid for separately stays.`
                        : undefined,
                    )
                  }
                >
                  {user.comped ? '✓ Free access to everything' : 'Give free access to everything'}
                </button>

                {/* One toggle per language, for the narrower case. */}
                <div className="lang-grants" style={{ opacity: user.comped ? 0.45 : 1 }}>
                  {LANGUAGE_LIST.map((entry) => {
                    const held = (user.languages ?? []).includes(entry.code)
                    return (
                      <button
                        key={entry.code}
                        type="button"
                        className={`grant-chip ${held ? 'is-on' : ''}`}
                        disabled={busy === user.id}
                        title={
                          held
                            ? `${entry.name} — subscribed. Click to remove.`
                            : `${entry.name} — not subscribed. Click to add.`
                        }
                        onClick={() => void toggleLanguage(user, entry.code, held)}
                      >
                        {entry.nativeName}
                      </button>
                    )
                  })}
                </div>
              </div>

              <div className="row">
                <button
                  type="button"
                  className="btn is-small"
                  disabled={working}
                  onClick={() =>
                    void act(
                      user,
                      'reset-password',
                      `Generate a new password for ${user.email}? Their current one stops working.`,
                    )
                  }
                >
                  New password
                </button>

                <button
                  type="button"
                  className="btn is-small"
                  disabled={working || isSelf}
                  title={isSelf ? 'You cannot suspend your own account' : undefined}
                  onClick={() =>
                    void act(
                      user,
                      user.disabled ? 'enable' : 'disable',
                      user.disabled
                        ? undefined
                        : `Suspend ${user.email}? They are signed out of every device immediately. Their islands are kept.`,
                    )
                  }
                >
                  {user.disabled ? 'Restore' : 'Suspend'}
                </button>

                <button
                  type="button"
                  className="btn is-small is-quiet"
                  disabled={working || isSelf}
                  onClick={() =>
                    void act(user, user.isAdmin ? 'revoke-admin' : 'grant-admin')
                  }
                >
                  {user.isAdmin ? 'Remove admin' : 'Make admin'}
                </button>

                <button
                  type="button"
                  className="btn is-small is-quiet is-danger"
                  disabled={working || isSelf}
                  onClick={() => void remove(user)}
                >
                  Delete
                </button>
              </div>
            </div>
          )
        })}

        {users?.length === 0 && <div className="empty">No accounts yet.</div>}
      </div>
    </>
  )
}
